import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Barcode, CheckCircle2, ExternalLink, Image as ImageIcon, RefreshCw, Send } from 'lucide-react';
import { Alert, Badge, Button, Panel, PanelBody } from '../ui/index.js';
import { cn } from '../ui/cn.js';
import { fetchJson } from '../lib/fetch-json.js';

/**
 * Comparatif GTIN Site ⇄ App — one screen to see, per product, the barcode
 * currently published on the site versus the approved App GTIN, and push the
 * App GTIN to the site (verified WAL, both fields, read-back) BEFORE the
 * nightly mirror refreshes the site-side value. Reuses the corrections list
 * (site ≠ app is its exact definition) and the direct push endpoint.
 */

interface CorrectionRow {
  readonly id: number;
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly image: string | null;
  readonly oldBarcode: string | null;
  readonly newBarcode: string | null;
}

function digitsOf(v: string | null | undefined): string {
  return (v ?? '').replace(/\D/g, '');
}

export function GtinComparePage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [brand, setBrand] = useState<string>('all');
  const [sentIds, setSentIds] = useState<readonly number[]>([]);
  const [bulkProgress, setBulkProgress] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['gtin-compare', brand],
    queryFn: () =>
      fetchJson<readonly CorrectionRow[]>(
        `/api/v1/review/corrections${brand !== 'all' ? `?brand=${encodeURIComponent(brand)}` : ''}`,
      ),
    refetchInterval: 60_000,
  });

  const brandsQuery = useQuery({
    queryKey: ['gtin-compare-brands'],
    queryFn: () =>
      fetchJson<{
        readonly brands: readonly { readonly brand: string | null; readonly count: number }[];
      }>('/api/v1/review/correction-brands'),
  });

  const push = useMutation({
    mutationFn: (productIds: readonly number[]) =>
      fetchJson<{ batchId: number; verified: number; failed: number; errors: readonly string[] }>(
        '/api/v1/wc-sync/push',
        {
          method: 'POST',
          body: JSON.stringify({ productIds: [...productIds] }),
          signal: AbortSignal.timeout(150_000),
        },
      ),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['gtin-compare'] });
      void queryClient.invalidateQueries({ queryKey: ['gtin-compare-brands'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      if (res.failed > 0) {
        setBulkProgress(`⚠ ${res.verified} envoyé(s), ${res.failed} échec(s) — ${res.errors.slice(0, 2).join(' · ')}`);
      }
    },
  });

  const rows = listQuery.data ?? [];
  const siteEmpty = rows.filter((r) => digitsOf(r.oldBarcode) === '').length;
  const siteDiff = rows.length - siteEmpty;

  const sendOne = (id: number): void => {
    setSentIds((cur) => (cur.includes(id) ? cur : [...cur, id]));
    push.mutate([id], {
      onSettled: () => setSentIds((cur) => cur.filter((x) => x !== id)),
    });
  };

  const sendAllPage = async (): Promise<void> => {
    const ids = rows.map((r) => r.id);
    const CHUNK = 5;
    for (let i = 0; i < ids.length; i += CHUNK) {
      setBulkProgress(`Envoi ${Math.min(i + CHUNK, ids.length)} / ${ids.length}…`);
      try {
        await push.mutateAsync(ids.slice(i, i + CHUNK));
      } catch {
        setBulkProgress(null);
        return;
      }
    }
    setBulkProgress(null);
  };

  return (
    <div className="space-y-5 pb-16">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-herb-100 border border-herb-600/20 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-herb-800">
            GTIN · Site ⇄ App
          </span>
        </div>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink font-display">
          Comparatif GTIN Site ⇄ App
        </h1>
        <p className="text-sm text-ink-soft">
          Le code actuellement publié sur le site comparé au GTIN approuvé de
          l&apos;application. Envoyer écrit le GTIN de l&apos;app sur le site
          (les 2 champs, relecture incluse).{' '}
          <strong>
            La synchro nocturne n&apos;efface jamais le GTIN de l&apos;app
          </strong>{' '}
          — elle rafraîchit seulement la colonne « Site » depuis Woo.
        </p>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-3.5 shadow-xs">
          <div className="flex items-center justify-between text-ink-ghost">
            <span className="text-xs font-semibold uppercase tracking-wider">Divergences</span>
            <Barcode className="h-4 w-4 text-slate-500" />
          </div>
          <div className="mt-2 text-2xl font-extrabold text-ink font-mono">{rows.length}</div>
        </div>
        <div className="rounded-xl border border-rust-600/30 bg-rust-50/50 p-3.5 shadow-xs">
          <div className="flex items-center justify-between text-rust-700">
            <span className="text-xs font-semibold uppercase tracking-wider">Site sans code</span>
            <ImageIcon className="h-4 w-4" />
          </div>
          <div className="mt-2 text-2xl font-extrabold text-rust-700 font-mono">{siteEmpty}</div>
        </div>
        <div className="rounded-xl border border-amber-300 bg-amber-50/50 p-3.5 shadow-xs">
          <div className="flex items-center justify-between text-amber-800">
            <span className="text-xs font-semibold uppercase tracking-wider">Code site différent</span>
            <AlertCircle className="h-4 w-4" />
          </div>
          <div className="mt-2 text-2xl font-extrabold text-amber-900 font-mono">{siteDiff}</div>
        </div>
      </div>

      <Panel>
        <PanelBody className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              Marque
              <select
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="h-9 min-w-52 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
              >
                <option value="all">Toutes les marques</option>
                {brandsQuery.data?.brands.map((b) => (
                  <option key={b.brand ?? '__none__'} value={b.brand ?? '__none__'}>
                    {(b.brand ?? '— Sans marque —') + ` (${b.count})`}
                  </option>
                ))}
              </select>
            </label>
            <span className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              disabled={listQuery.isFetching}
              onClick={() => void listQuery.refetch()}
            >
              <RefreshCw className={cn('h-4 w-4', listQuery.isFetching && 'animate-spin')} />
              Actualiser
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={rows.length === 0 || push.isPending || bulkProgress !== null}
              onClick={() => void sendAllPage()}
              title="Envoie les GTIN de l'app sur le site pour tous les produits listés (tranches de 25, chaque fiche relue après écriture)"
            >
              <Send className="h-4 w-4" />
              {bulkProgress ?? `Tout envoyer vers le site (${rows.length})`}
            </Button>
          </div>
          {push.isError && (
            <Alert tone="alarm" title="Échec de l'envoi">
              {String(push.error.message)}
            </Alert>
          )}
          {bulkProgress !== null && push.isPending && (
            <Alert tone="info" title="Envoi en cours">
              {bulkProgress}
            </Alert>
          )}
          {push.isSuccess && push.data.failed === 0 && bulkProgress === null && (
            <Alert tone="good" title="Envoi vérifié">
              {push.data.verified} produit(s) écrit(s) et relu(s) sur le site (lot #
              {push.data.batchId}).
            </Alert>
          )}
        </PanelBody>
      </Panel>

      <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-xs">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-cream/40 text-[10px] uppercase tracking-wider text-ink-faint">
              <th className="px-4 py-2.5 font-semibold">Produit</th>
              <th className="px-3 py-2.5 font-semibold">GTIN publié sur le site</th>
              <th className="px-3 py-2.5 font-semibold">GTIN approuvé (App)</th>
              <th className="px-3 py-2.5 text-center font-semibold">État</th>
              <th className="px-3 py-2.5 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => {
              const siteEmpty = digitsOf(row.oldBarcode) === '';
              const busy = sentIds.includes(row.id) || push.isPending;
              return (
                <tr key={row.id} className="hover:bg-cream/30">
                  <td className="max-w-sm px-4 py-2.5">
                    <div className="flex items-center gap-2.5">
                      {row.image !== null && row.image !== '' ? (
                        <img
                          src={row.image}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-lg border border-border object-cover bg-cream"
                        />
                      ) : (
                        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-dashed border-border bg-cream text-ink-ghost">
                          <ImageIcon className="h-4 w-4 opacity-40" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-ink">{row.name}</p>
                        <p className="font-mono text-[11px] text-ink-faint">
                          {row.sku ?? '∅'} · {row.brand ?? '—'}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    {siteEmpty ? (
                      <span className="font-mono text-xs text-rust-700">∅ (aucun code sur le site)</span>
                    ) : (
                      <span className="font-mono text-xs text-rust-700 line-through">{row.oldBarcode}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="font-mono text-xs font-bold text-herb-700">{row.newBarcode}</span>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {siteEmpty ? (
                      <Badge tone="alarm">site sans code</Badge>
                    ) : (
                      <Badge tone="signal">code différent</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <Button
                        variant="soft"
                        size="sm"
                        disabled={busy}
                        onClick={() => sendOne(row.id)}
                        title="Écrit le GTIN de l'app sur le site (global_unique_id + REF plugin), puis relit la fiche"
                      >
                        <Send className="h-3.5 w-3.5" />
                        {sentIds.includes(row.id) ? 'Envoi…' : 'Envoyer'}
                      </Button>
                      {row.sku !== null && (
                        <a
                          href={`https://www.maparatunisie.tn/?s=${encodeURIComponent(row.sku)}&post_type=product`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-700 hover:text-sky-600"
                          title="Voir sur le site"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {listQuery.data !== undefined && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-ink-ghost">
                  <CheckCircle2 className="mx-auto mb-2 h-6 w-6 opacity-40" />
                  Site et App sont alignés — plus aucune divergence GTIN.
                </td>
              </tr>
            )}
            {listQuery.isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-sm text-ink-ghost">
                  Lecture des divergences…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-ink-faint">
        Même pipeline que « À corriger (Woo) » :
        journal avant/après + relecture des 2 champs. La cible (staging /
        production) est celle de la page S10.
      </p>
    </div>
  );
}
